/**
 * @file: index.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * The internal-discussion section on the lead-detail screen: a blog-comment
 * style thread of internal staff notes with media. The composer is hidden by
 * default and revealed by the header's "+ Post" action, sitting at the top of
 * the card (divided from the list by a hairline). Authors may delete their own
 * comments. Lives inline in the detail ScrollView (no realtime/unread).
 */

import { useCurrentUser } from '@euler/app/flows/auth/context';
import { Card } from '@euler/app/flows/leads/pages/detail/components/common/Card';
import { SectionAction } from '@euler/app/flows/leads/pages/detail/components/common/SectionAction';
import { SectionHeader } from '@euler/app/flows/leads/pages/detail/components/common/SectionHeader';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { EmptyView } from '@euler/components/EmptyView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { LeadMediaAttachmentInput } from '@euler/model/lead/attachment';
import { idEquals } from '@euler/utils';
import { useCallback, useState } from 'react';
import { View } from 'react-native';
import { CommentRow } from './CommentRow';
import { Composer } from './Composer';
import { useLeadDiscussion } from './functions/useLeadDiscussion';

export const LeadDiscussionSection = ({ leadId }: { leadId: string }) => {
  const { rpx } = useResponsiveContext();
  const currentUser = useCurrentUser();
  const confirm = useConfirm();
  const discussion = useLeadDiscussion(leadId);
  const myMemberId = currentUser?.user.memberId;
  const [composerOpen, setComposerOpen] = useState(false);

  const onDelete = useCallback(
    async (id: string) => {
      const ok = await confirm({ title: '删除该讨论?', destructive: true });
      if (!ok) return;
      await discussion.remove(id);
    },
    [confirm, discussion],
  );

  // Collapse the composer once a post lands.
  const onSend = useCallback(
    async (body: string, attachments: LeadMediaAttachmentInput[]) => {
      await discussion.post(body, attachments);
      setComposerOpen(false);
    },
    [discussion],
  );

  const { messages } = discussion;

  return (
    <>
      <SectionHeader
        title="讨论区"
        right={
          <SectionAction
            label="发表"
            onPress={() => setComposerOpen(open => !open)}
          />
        }
      />
      <Card>
        <View style={{ paddingVertical: rpx(2) }}>
          {composerOpen ? (
            <View
              style={{
                borderBottomWidth: 1,
                borderBottomColor: LD.hairline,
                paddingBottom: rpx(12),
                marginBottom: rpx(2),
              }}
            >
              <Composer onSend={onSend} />
            </View>
          ) : null}

          {messages.length ? (
            messages.map((message, index) => (
              <CommentRow
                key={message.id}
                first={index === 0}
                message={message}
                canDelete={
                  myMemberId != null &&
                  !message.id.startsWith('temp:') &&
                  idEquals(message.author.id, myMemberId)
                }
                onDelete={onDelete}
              />
            ))
          ) : (
            <EmptyView
              noFullHeight
              showIcon={false}
              message="暂无讨论，来说点什么"
              style={{ paddingVertical: rpx(20) }}
              messageStyle={{ color: LD.mist, fontSize: rpx(13) }}
            />
          )}
        </View>
      </Card>
    </>
  );
};
